import React, { useState, useRef, useEffect, useMemo } from 'react';
import { SearchIcon, XIcon, PlusIcon, ProductIcon } from '../../../../components/icons';
import { formatCurrency } from '../../../../utils/formatters';
import Input from '../../../../components/ui/Input';

interface ProductSelectorProps {
    isOpen: boolean;
    onClose: () => void;
    availableProducts: Array<{ id: string; name: string; value: number }>;
    onSelect: (product: { id: string; name: string; value: number }) => void;
    onCreateNew: () => void;
}

export const ProductSelector: React.FC<ProductSelectorProps> = ({
    isOpen,
    onClose,
    availableProducts,
    onSelect,
    onCreateNew,
}) => {
    const [searchTerm, setSearchTerm] = useState('');
    const searchInputRef = useRef<HTMLInputElement | null>(null);

    useEffect(() => {
        if (isOpen) {
            setTimeout(() => searchInputRef.current?.focus(), 50);
        }
    }, [isOpen]);

    const filteredProducts = useMemo(
        () =>
            availableProducts.filter((p) =>
                p.name.toLowerCase().includes(searchTerm.toLowerCase()),
            ),
        [availableProducts, searchTerm],
    );

    const handleSelect = (product: { id: string; name: string; value: number }) => {
        onSelect(product);
        setSearchTerm('');
    };

    if (!isOpen) return null;

    return (
        <div
            role="listbox"
            aria-label="Selecionar produto"
            className="absolute right-0 top-s8 z-50 flex max-h-[300px] w-72 origin-top-right flex-col overflow-hidden rounded-lg border border-subtle bg-surface-0 shadow-lg animate-in fade-in zoom-in-95 duration-100"
        >
            <div className="sticky top-0 border-b border-subtle bg-surface-0 p-s2">
                <div className="relative">
                    <Input
                        ref={searchInputRef}
                        type="text"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder="Buscar produto..."
                        aria-label="Buscar produto"
                        inputSize="sm"
                        icon={<SearchIcon className="size-4" />}
                        containerClassName="w-full"
                        className="!pr-s8"
                    />
                    <button
                        type="button"
                        onClick={onClose}
                        className="absolute right-s2 top-s2 z-10 text-muted transition-colors hover:text-secondary"
                        aria-label="Fechar"
                    >
                        <XIcon className="size-4" aria-hidden />
                    </button>
                </div>
            </div>

            <div className="max-h-[200px] flex-1 overflow-y-auto p-s1 scrollbar-thin">
                {filteredProducts.length > 0 ? (
                    filteredProducts.map((product) => (
                        <button
                            key={product.id}
                            type="button"
                            role="option"
                            onClick={() => handleSelect(product)}
                            className="group flex w-full items-center justify-between rounded-md px-s3 py-s2 text-left text-sm text-default transition-colors hover:bg-surface-subtle"
                        >
                            <div className="flex min-w-0 items-center gap-s2 truncate">
                                <span className="rounded border border-transparent bg-surface-subtle p-s1 text-muted group-hover:border-subtle group-hover:bg-surface-0">
                                    <ProductIcon className="size-3" aria-hidden />
                                </span>
                                <span className="max-w-[140px] truncate" title={product.name}>
                                    {product.name}
                                </span>
                            </div>
                            <span className="whitespace-nowrap rounded bg-surface-subtle px-s2 py-s1 font-mono text-xs font-semibold tabular-nums text-muted group-hover:border group-hover:border-subtle group-hover:bg-surface-0">
                                {formatCurrency(product.value)}
                            </span>
                        </button>
                    ))
                ) : (
                    <p className="px-s4 py-s8 text-center text-xs text-muted">
                        Nenhum produto encontrado.
                    </p>
                )}
            </div>

            <div className="sticky bottom-0 border-t border-subtle bg-surface-subtle p-s2">
                <button
                    type="button"
                    onClick={onCreateNew}
                    className="flex min-h-11 w-full touch-manipulation items-center justify-center gap-s2 rounded-md border border-subtle bg-surface-0 px-s3 py-s2 text-sm font-medium text-primary shadow-sm transition-all hover:border-brand-200 hover:bg-primary-subtle md:min-h-0"
                >
                    <PlusIcon className="size-4" aria-hidden /> Criar novo produto
                </button>
            </div>
        </div>
    );
};
